import React, { useState, useEffect } from 'react';
import { 
  BarChart3, TrendingUp, TrendingDown, Users, ShoppingCart, 
  Eye, MousePointerClick, Target, AlertTriangle, Star, Download,
  RefreshCw, ToggleLeft, ToggleRight, ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple card component
const MetricCard = ({ title, value, subtitle, icon: Icon, trend, color = '#B78C3C' }) => (
  <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-100">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs text-stone-500 uppercase tracking-wide">{title}</p>
        <p className="text-2xl font-bold mt-1" style={{ color }}>{value}</p>
        {subtitle && <p className="text-xs text-stone-400 mt-1">{subtitle}</p>}
      </div>
      <div 
        className="w-10 h-10 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon size={20} style={{ color }} />
      </div>
    </div>
    {trend && (
      <div className={`flex items-center gap-1 mt-2 text-xs ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
        {trend > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
        <span>{Math.abs(trend)}% vs anterior</span>
      </div>
    )}
  </div>
);

// Funnel visualization
const FunnelChart = ({ data }) => {
  if (!data || data.length === 0) return null;
  
  const maxCount = Math.max(...data.map(d => d.count));
  
  return (
    <div className="space-y-2">
      {data.map((stage, idx) => (
        <div key={stage.stage} className="relative">
          <div className="flex items-center gap-3">
            <div className="w-24 text-sm text-stone-600">{stage.stage}</div>
            <div className="flex-1 relative h-8">
              <div 
                className="absolute inset-y-0 left-0 rounded-r-lg transition-all duration-500"
                style={{ 
                  width: `${(stage.count / maxCount) * 100}%`,
                  backgroundColor: `hsl(${140 - idx * 30}, 40%, ${50 + idx * 5}%)`,
                  minWidth: '20px'
                }}
              />
              <span className="absolute inset-y-0 left-2 flex items-center text-sm font-medium text-white">
                {stage.count.toLocaleString()}
              </span>
            </div>
            <div className="w-16 text-sm text-right text-stone-500">
              {(stage.rate * 100).toFixed(1)}%
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Product ranking table
const ProductRanking = ({ products, title, metric, onRefresh }) => (
  <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-100">
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-medium text-stone-800">{title}</h3>
      <span className="text-xs text-stone-400 uppercase">{metric}</span>
    </div>
    <div className="space-y-2 max-h-64 overflow-y-auto">
      {products.slice(0, 10).map((product, idx) => (
        <div 
          key={product.product_id}
          className="flex items-center gap-3 p-2 rounded-lg hover:bg-stone-50"
        >
          <span className="w-6 h-6 rounded-full bg-stone-100 flex items-center justify-center text-xs font-medium">
            {idx + 1}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{product.product_id}</p>
            <p className="text-xs text-stone-400">
              {product.opens || 0} aperturas · {product.adds || 0} añadidos
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm font-bold" style={{ color: '#B78C3C' }}>
              {((product[metric] || 0) * 100).toFixed(1)}%
            </p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Alert component
const AlertCard = ({ alert }) => {
  const colors = {
    warning: { bg: '#FEF3C7', text: '#92400E', icon: AlertTriangle },
    info: { bg: '#DBEAFE', text: '#1E40AF', icon: Star },
    error: { bg: '#FEE2E2', text: '#991B1B', icon: AlertTriangle }
  };
  const style = colors[alert.severity] || colors.info;
  const Icon = style.icon;
  
  return (
    <div 
      className="p-3 rounded-lg flex items-start gap-3"
      style={{ backgroundColor: style.bg }}
    >
      <Icon size={18} style={{ color: style.text }} />
      <div>
        <p className="text-sm font-medium" style={{ color: style.text }}>
          {alert.product_id}
        </p>
        <p className="text-xs" style={{ color: style.text, opacity: 0.8 }}>
          {alert.message}
        </p>
      </div>
    </div>
  );
};

// Main Dashboard Component
export default function IntelligenceDashboard() {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [rankings, setRankings] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [settings, setSettings] = useState({
    auto_order: false,
    smart_suggestions: false,
    lang_personalization: false,
    mark_recommended: false
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [metricsRes, funnelRes, rankingsRes, alertsRes, settingsRes] = await Promise.all([
        fetch(`${API_URL}/api/analytics/admin/metrics/latest`, { headers }),
        fetch(`${API_URL}/api/analytics/admin/metrics/funnel`, { headers }),
        fetch(`${API_URL}/api/analytics/admin/metrics/products/ranking?metric=add_rate&limit=20`, { headers }),
        fetch(`${API_URL}/api/analytics/admin/metrics/alerts`, { headers }),
        fetch(`${API_URL}/api/analytics/admin/optimization/settings`, { headers })
      ]);
      
      const metricsData = await metricsRes.json();
      const funnelData = await funnelRes.json();
      const rankingsData = await rankingsRes.json();
      const alertsData = await alertsRes.json();
      const settingsData = await settingsRes.json();
      
      setMetrics(metricsData);
      setFunnel(funnelData.funnel || []);
      setRankings(rankingsData.rankings || []);
      setAlerts(alertsData.alerts || []);
      setSettings(settingsData);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const recalculateMetrics = async () => {
    setCalculating(true);
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${API_URL}/api/analytics/admin/metrics/calculate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      await fetchData();
    } catch (error) {
      console.error('Error calculating metrics:', error);
    } finally {
      setCalculating(false);
    }
  };

  const updateSetting = async (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${API_URL}/api/analytics/admin/optimization/settings`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newSettings)
      });
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  const runOptimization = async () => {
    setOptimizing(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/analytics/admin/optimization/run`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      console.log('Optimization result:', result);
      alert(`Optimización completada: ${result.actions?.length || 0} acciones ejecutadas`);
    } catch (error) {
      console.error('Error running optimization:', error);
      alert('Error al ejecutar la optimización');
    } finally {
      setOptimizing(false);
    }
  };

  const exportCSV = () => {
    if (!metrics) return;
    
    const products = Object.entries(metrics.products || {}).map(([id, data]) => ({
      product_id: id,
      views: data.views,
      opens: data.opens,
      adds: data.adds,
      add_rate: (data.add_rate * 100).toFixed(2) + '%',
      suggestion_adds: data.suggestion_adds
    }));
    
    const headers = Object.keys(products[0] || {}).join(',');
    const rows = products.map(p => Object.values(p).join(',')).join('\n');
    const csv = headers + '\n' + rows;
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pont10_metrics_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  useEffect(() => {
    fetchData();
  }, []);

  const global = metrics?.global || {};

  return (
    <div className="min-h-screen bg-stone-50 p-4 md:p-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/admin/dashboard')}
              className="p-2 hover:bg-stone-200 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-stone-800">Inteligencia Comercial</h1>
              <p className="text-sm text-stone-500">
                {metrics?.calculated_at 
                  ? `Última actualización: ${new Date(metrics.calculated_at).toLocaleString()}`
                  : 'Sin datos aún'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={recalculateMetrics}
              disabled={calculating}
              className="flex items-center gap-2 px-4 py-2 bg-stone-800 text-white rounded-lg hover:bg-stone-700 disabled:opacity-50"
            >
              <RefreshCw size={16} className={calculating ? 'animate-spin' : ''} />
              {calculating ? 'Calculando...' : 'Recalcular'}
            </button>
            <button
              onClick={exportCSV}
              className="flex items-center gap-2 px-4 py-2 border border-stone-300 rounded-lg hover:bg-stone-100"
            >
              <Download size={16} />
              Exportar CSV
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw size={32} className="animate-spin text-stone-400" />
          </div>
        ) : !metrics || metrics.error ? (
          <div className="text-center py-20">
            <BarChart3 size={48} className="mx-auto text-stone-300 mb-4" />
            <p className="text-stone-500">No hay métricas disponibles</p>
            <button
              onClick={recalculateMetrics}
              className="mt-4 px-4 py-2 bg-stone-800 text-white rounded-lg"
            >
              Calcular métricas
            </button>
          </div>
        ) : (
          <>
            {/* Global Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <MetricCard
                title="Sesiones"
                value={global.total_sessions?.toLocaleString() || 0}
                icon={Users}
              />
              <MetricCard
                title="Añaden productos"
                value={`${((global.add_rate || 0) * 100).toFixed(1)}%`}
                subtitle={`${global.sessions_with_add || 0} sesiones`}
                icon={ShoppingCart}
                color="#B78C3C"
              />
              <MetricCard
                title="Conversión"
                value={`${((global.conversion_rate || 0) * 100).toFixed(1)}%`}
                subtitle="Mostrar al camarero"
                icon={Target}
                color="#B78C3C"
              />
              <MetricCard
                title="Tiempo medio sesión"
                value={`${Math.round((global.avg_session_duration_ms || 0) / 1000)}s`}
                icon={Eye}
                color="#5C4A30"
              />
            </div>

            {/* Main Content Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {/* Funnel */}
              <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-100 lg:col-span-2">
                <h3 className="font-medium text-stone-800 mb-4">Funnel de Conversión</h3>
                <FunnelChart data={funnel} />
              </div>

              {/* Alerts */}
              <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-100">
                <h3 className="font-medium text-stone-800 mb-4">Alertas Automáticas</h3>
                {alerts.length === 0 ? (
                  <p className="text-sm text-stone-400 text-center py-8">
                    Sin alertas activas
                  </p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {alerts.map((alert, idx) => (
                      <AlertCard key={idx} alert={alert} />
                    ))}
                  </div>
                )}
              </div>

              {/* Product Rankings */}
              <ProductRanking
                products={rankings}
                title="Top Productos"
                metric="add_rate"
              />

              {/* By Language */}
              <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-100">
                <h3 className="font-medium text-stone-800 mb-4">Rendimiento por Idioma</h3>
                <div className="space-y-3">
                  {Object.entries(metrics.by_language || {}).map(([lang, data]) => (
                    <div key={lang} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">
                          {lang === 'es' ? '🇪🇸' : lang === 'en' ? '🇬🇧' : '🏴󠁥󠁳󠁣󠁴󠁿'}
                        </span>
                        <span className="text-sm font-medium capitalize">{lang}</span>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold">{data.sessions} sesiones</p>
                        <p className="text-xs text-stone-400">
                          {((data.conversion_rate || 0) * 100).toFixed(1)}% conversión
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* By Section */}
              <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-100">
                <h3 className="font-medium text-stone-800 mb-4">Rendimiento por Sección</h3>
                <div className="space-y-3">
                  {Object.entries(metrics.by_section || {}).map(([section, data]) => (
                    <div key={section} className="flex items-center justify-between">
                      <span className="text-sm font-medium capitalize">{section}</span>
                      <div className="text-right">
                        <p className="text-sm font-bold">{data.views} vistas</p>
                        <p className="text-xs text-stone-400">
                          {data.adds} añadidos ({((data.conversion_rate || 0) * 100).toFixed(1)}%)
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Settings Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-stone-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-stone-800">Configuración de Optimización</h3>
                <button
                  onClick={runOptimization}
                  disabled={optimizing}
                  className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {optimizing ? (
                    <>
                      <RefreshCw size={14} className="animate-spin" />
                      Optimizando...
                    </>
                  ) : (
                    <>
                      <Target size={14} />
                      Ejecutar Optimización
                    </>
                  )}
                </button>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Orden automático</p>
                    <p className="text-xs text-stone-400">Reordena por add_rate</p>
                  </div>
                  <button
                    onClick={() => updateSetting('auto_order', !settings.auto_order)}
                    className="text-stone-600"
                  >
                    {settings.auto_order ? <ToggleRight size={28} className="text-green-600" /> : <ToggleLeft size={28} />}
                  </button>
                </div>
                <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Sugerencias inteligentes</p>
                    <p className="text-xs text-stone-400">Por combinaciones</p>
                  </div>
                  <button
                    onClick={() => updateSetting('smart_suggestions', !settings.smart_suggestions)}
                    className="text-stone-600"
                  >
                    {settings.smart_suggestions ? <ToggleRight size={28} className="text-green-600" /> : <ToggleLeft size={28} />}
                  </button>
                </div>
                <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Por idioma</p>
                    <p className="text-xs text-stone-400">Prioriza por idioma</p>
                  </div>
                  <button
                    onClick={() => updateSetting('lang_personalization', !settings.lang_personalization)}
                    className="text-stone-600"
                  >
                    {settings.lang_personalization ? <ToggleRight size={28} className="text-green-600" /> : <ToggleLeft size={28} />}
                  </button>
                </div>
                <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Marcar recomendados</p>
                    <p className="text-xs text-stone-400">Auto-destacar estrellas</p>
                  </div>
                  <button
                    onClick={() => updateSetting('mark_recommended', !settings.mark_recommended)}
                    className="text-stone-600"
                  >
                    {settings.mark_recommended ? <ToggleRight size={28} className="text-green-600" /> : <ToggleLeft size={28} />}
                  </button>
                </div>
              </div>
              {settings.last_optimization && (
                <p className="text-xs text-stone-400 mt-3">
                  Última optimización: {new Date(settings.last_optimization).toLocaleString()}
                </p>
              )}
            </div>

            {/* Top Combinations */}
            {metrics.top_combinations?.length > 0 && (
              <div className="mt-6 bg-white rounded-xl p-4 shadow-sm border border-stone-100">
                <h3 className="font-medium text-stone-800 mb-4">Top Combinaciones de Productos</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {metrics.top_combinations.slice(0, 6).map((combo, idx) => (
                    <div key={idx} className="p-3 bg-stone-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">🔗</span>
                        <span className="text-sm font-medium">{combo.count}x juntos</span>
                      </div>
                      <div className="text-xs text-stone-500">
                        {combo.products.join(' + ')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
